/**
 * Time utility functions for consistent date/time formatting
 */

/**
 * Get current local browser time as formatted string
 * Format: MM/DD/YYYY HH:MM:SS (includes seconds for accurate sorting)
 */
export function getLocalTimeString(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Format ISO timestamp to local time string
 * Format: MM/DD/YYYY HH:MM:SS (includes seconds for accurate sorting)
 */
export function formatToLocalTimeString(isoString: string): string {
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Format timestamp for display (removes seconds)
 * Format: MM/DD/YYYY HH:MM
 */
export function formatForDisplay(timeString: string): string {
  // Remove seconds if present (format: MM/DD/YYYY HH:MM:SS -> MM/DD/YYYY HH:MM)
  if (timeString.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/)) {
    return timeString.substring(0, 16) // Remove last 3 characters (":SS")
  }
  // If already in MM/DD/YYYY HH:MM format, return as-is
  return timeString
}

/**
 * Parse date string (either ISO or MM/DD/YYYY HH:MM:SS or MM/DD/YYYY HH:MM) to timestamp
 * Used for sorting and comparison
 */
export function parseDateToTimestamp(dateString: string): number {
  // Check if it's in MM/DD/YYYY HH:MM:SS or MM/DD/YYYY HH:MM format
  if (dateString.includes('/') && dateString.includes(':')) {
    // Parse MM/DD/YYYY HH:MM:SS or MM/DD/YYYY HH:MM format
    const [datePart, timePart] = dateString.split(' ')
    const [month, day, year] = datePart.split('/').map(Number)
    const timeParts = timePart.split(':')
    const hours = Number(timeParts[0])
    const minutes = Number(timeParts[1])
    const seconds = timeParts[2] ? Number(timeParts[2]) : 0 // Default to 0 if seconds not present
    return new Date(year, month - 1, day, hours, minutes, seconds).getTime()
  } else {
    // Parse ISO string
    return new Date(dateString).getTime()
  }
}

